import type { ChartConfiguration } from 'chart.js'

export interface ViewControls {
  enable_fullscreen_btn?: boolean
  enable_chart_type_switcher?: boolean
  enable_data_entry?: boolean
}

export type ChartType = 'bar' | 'line' | 'pie' | 'doughnut' | 'radar' | 'polarArea' | 'scatter' | 'bubble' | 'area'

// Standalone chart types that don't support multiple series mixing
export const STANDALONE_CHART_TYPES: ChartType[] = ['pie', 'doughnut', 'radar', 'polarArea']

// Chart types that can be mixed together (Cartesian charts)
export const MIXABLE_CHART_TYPES: ChartType[] = ['bar', 'line', 'scatter', 'bubble', 'area']

// 1D chart types (label/value based) - can be mixed with each other
export const ONE_D_CHART_TYPES: ChartType[] = ['bar', 'line', 'area']

// 2D chart types (x/y coordinate based) - scatter only
export const TWO_D_CHART_TYPES: ChartType[] = ['scatter']

// 3D chart types (x/y/r coordinate based) - bubble only
export const THREE_D_CHART_TYPES: ChartType[] = ['bubble']

// XY chart types that use x/y coordinates instead of label/value (2D)
export const XY_CHART_TYPES: ChartType[] = ['scatter']

// XYR chart types that use x/y/r coordinates (3D - bubble only)
export const XYR_CHART_TYPES: ChartType[] = ['bubble']

export interface SeriesDataPoint {
  label: string
  value: string
  x_value?: string
  y_value?: string
  r_value?: string // Radius/size value for bubble charts
  dynamic_value?: {
    path: string
    value: string
  }
  dynamic_x_value?: {
    path: string
    value: string
  }
  dynamic_y_value?: {
    path: string
    value: string
  }
  dynamic_r_value?: {
    path: string
    value: string
  }
  array_config?: {
    labelField: string
    valueField: string
    xField?: string
    yField?: string
    rField?: string
  }
}

export interface ChartSeries {
  id: string
  name: string
  chart_type: ChartType
  color?: string // Optional, color is determined by color scheme
  data_points: SeriesDataPoint[]
}

export interface ExtendedChartConfiguration extends ChartConfiguration {
  // Legacy single-series data points (for backward compatibility)
  data_points?: SeriesDataPoint[]
  // New multi-series support
  series?: ChartSeries[]
  context?: any
  data?: ChartConfiguration['data'] & {
    datasets?: Array<{
      label?: string
      type?: string
      background_color?: string | string[]
      border_color?: string | string[]
      point_style?: string
      fill?: boolean
      tension?: number
      show_line?: boolean
    }>
  }
  options?: ChartConfiguration['options'] & {
    elements?: {
      point?: {
        point_style?: string
      }
    }
    scales?: {
      x?: {
        title?: {
          text?: string
          display?: boolean
        }
      }
      y?: {
        title?: {
          text?: string
          display?: boolean
        }
        begin_at_zero?: boolean
      }
      r?: {
        begin_at_zero?: boolean
        ticks?: {
          step_size?: number
        }
      }
    }
    index_axis?: 'x' | 'y'
  }
  color_scheme_type?: 'theme' | 'custom'
  color_scheme?: string[]
  percent_display?: boolean
  view_controls?: ViewControls
  chart_height?: number
  chart_width?: number
  // Store the primary chart type (from Series 1)
  _primary_chart_type?: ChartType
}

export interface DynamicValue {
  path: string
  value: string
}

export interface ArrayConfig {
  labelField: string
  valueField: string
  xField?: string
  yField?: string
  rField?: string
}

// Runtime data point used in the settings component
export interface DataPoint {
  label: string
  value: string
  xValue?: string
  yValue?: string
  rValue?: string // Radius/size value for bubble charts
  dynamicValue?: DynamicValue | any
  dynamicXValue?: DynamicValue | any
  dynamicYValue?: DynamicValue | any
  dynamicRValue?: DynamicValue | any
  selectedValue?: any
  selectedXValue?: any
  selectedYValue?: any
  selectedRValue?: any
  arrayConfig?: ArrayConfig
}

// Runtime series used in the settings component
export interface Series {
  id: string
  name: string
  chartType: ChartType
  dataPoints: DataPoint[]
}
